<template>
  <div v-if="!reports.length">
    <ReportEmptyState
      title="Nenhum relatório encontrado"
      description="Crie um novo relatório para iniciar o histórico de visitas técnicas."
      :icon="PhFileText"
    />
  </div>

  <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
    <article
      v-for="report in reports"
      :key="report.id"
      class="group rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:border-green-100 hover:shadow-md"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h3 class="font-semibold leading-tight text-gray-800">
            {{ report.title }}
          </h3>

          <p class="mt-1 text-xs text-gray-500">
            {{ getReportTypeLabel(report.report_type) }}
          </p>
        </div>

        <ReportStatusBadge :status="report.status" />
      </div>

      <div class="mt-5 space-y-3 text-sm">
        <div class="flex justify-between gap-4">
          <span class="text-gray-400">Empresa</span>
          <span class="text-right font-medium text-gray-700">
            {{ getClientName(report) }}
          </span>
        </div>

        <div class="flex justify-between gap-4">
          <span class="text-gray-400">Técnico</span>
          <span class="text-right font-medium text-gray-700">
            {{ report.technician_name || "-" }}
          </span>
        </div>

        <div class="flex justify-between gap-4">
          <span class="text-gray-400">Data da visita</span>
          <span class="text-right font-medium text-gray-700">
            {{ formatDate(report.visit_date) }}
          </span>
        </div>

        <div class="flex justify-between gap-4">
          <span class="text-gray-400">Criado em</span>
          <span class="text-right font-medium text-gray-700">
            {{ formatDate(report.created_at) }}
          </span>
        </div>
      </div>

      <div
        class="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4"
      >

        <button
          v-if="report.status === 'issued' && (report.pdf_url || report.pdf_path)"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('open-pdf', report)"
        >
          <PhFilePdf class="h-4 w-4" />
          Visualizar PDF
        </button>

        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('build', report)"
        >
          <PhWrench v-if="report.status === 'draft'" class="h-4 w-4" />
          <PhEye v-else class="h-4 w-4" />

          {{ report.status === "draft" ? "Montar" : "Visualizar" }}
        </button>

        <button
          v-if="report.status !== 'archived'"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-yellow-50 hover:text-yellow-600"
          @click="$emit('edit', report)"
        >
          <PhNotePencil class="h-4 w-4" />
          Editar
        </button>

        <button
          v-if="report.status === 'finished'"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-yellow-50 hover:text-yellow-700"
          @click="$emit('reopen', report)"
        >
          <PhArrowCounterClockwise class="h-4 w-4" />
          Reabrir
        </button>

        <button
          v-if="['finished', 'issued', 'archived'].includes(report.status)"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-blue-50 hover:text-blue-700"
          @click="$emit('clone', report)"
        >
          <PhCopy class="h-4 w-4" />
          Criar cópia
        </button>

        <button
          v-if="report.status === 'draft'"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('finish', report)"
        >
          <PhCheckCircle class="h-4 w-4" />
          Finalizar
        </button>

        <button
          v-if="report.status !== 'archived'"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-red-50 hover:text-red-600"
          @click="$emit('archive', report)"
        >
          <PhArchiveBox class="h-4 w-4" />
          Arquivar
        </button>

        <button
          v-else
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('restore', report)"
        >
          <PhArrowCounterClockwise class="h-4 w-4" />
          Restaurar
        </button>
      </div>
    </article>
  </div>
</template>

<script setup>
import {
  PhArchiveBox,
  PhArrowCounterClockwise,
  PhCheckCircle,
  PhFileText,
  PhNotePencil,
  PhEye,
  PhWrench,
  PhCopy,
  PhFilePdf,
} from "@phosphor-icons/vue";

import ReportEmptyState from "../shared/ReportEmptyState.vue";
import ReportStatusBadge from "./ReportStatusBadge.vue";

defineProps({
  reports: {
    type: Array,
    default: () => [],
  },
});

defineEmits([
  "build",
  "edit",
  "finish",
  "reopen",
  "archive",
  "restore",
  "open-pdf",
  "clone",
]);

function getClientName(report) {
  return (
    report?.client_snapshot?.trade_name ||
    report?.client_snapshot?.corporate_name ||
    report?.client?.trade_name ||
    report?.client?.corporate_name ||
    "-"
  );
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR").format(date);
}

function getReportTypeLabel(type) {
  const labels = {
    photographic: "Relatório fotográfico",
    descriptive: "Relatório descritivo",
    free_text: "Texto livre",
    photo_text: "Foto + texto",
    checklist: "Checklist",
    action_plan: "Plano de ação",
    audit: "Auditoria",
  };

  return labels[type] || "Relatório técnico";
}
</script>
